import { Directive, ElementRef, inject, input, output } from '@angular/core';

/** Distance horizontale (px) au-delà de laquelle le geste ferme le panneau. */
export const SWIPE_CLOSE_THRESHOLD_PX = 80;
/** Distance à partir de laquelle on décide si le geste est horizontal ou vertical. */
export const SWIPE_INTENT_PX = 10;

export type SwipeIntent = 'pending' | 'horizontal' | 'vertical';

/** Le geste est-il un glissement vers la droite (horizontal) ou un défilement (vertical) ? */
export function swipeIntent(dx: number, dy: number): SwipeIntent {
  if (Math.max(Math.abs(dx), Math.abs(dy)) < SWIPE_INTENT_PX) return 'pending';
  return dx > 0 && Math.abs(dx) > Math.abs(dy) * 1.2 ? 'horizontal' : 'vertical';
}

/** Un geste relâché ferme-t-il le panneau ? (assez long, vers la droite, plutôt horizontal) */
export function isCloseSwipe(dx: number, dy: number, threshold = SWIPE_CLOSE_THRESHOLD_PX): boolean {
  return swipeIntent(dx, dy) === 'horizontal' && dx >= threshold;
}

/**
 * Glisser vers la droite pour fermer un panneau latéral (pointer events, doigt uniquement).
 * Le panneau suit le doigt ; relâché au-delà de 80 px il émet `swipeClose`, sinon il revient.
 * Le défilement vertical reste natif : le contenu scrollable porte `touch-action: pan-y`, et un
 * geste décidé « vertical » est abandonné. N'agit que si `appSwipeClose` est vrai (mobile).
 */
@Directive({
  selector: '[appSwipeClose]',
  host: {
    '(pointerdown)': 'onDown($event)',
    '(pointermove)': 'onMove($event)',
    '(pointerup)': 'onUp($event)',
    '(pointercancel)': 'reset()',
  },
})
export class SwipeCloseDirective {
  readonly enabled = input(false, { alias: 'appSwipeClose' });
  readonly swipeClose = output<void>();

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private pointerId: number | null = null;
  private startX = 0;
  private startY = 0;
  private dx = 0;
  private intent: SwipeIntent = 'pending';

  onDown(event: PointerEvent): void {
    if (!this.enabled() || event.pointerType !== 'touch' || !event.isPrimary) return;
    // Un champ de saisie, une zone qui défile en largeur ou un curseur de réglage gardent le geste.
    const target = event.target as HTMLElement | null;
    if (target?.closest('input, textarea, select, [contenteditable="true"], [data-no-swipe]')) return;
    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.dx = 0;
    this.intent = 'pending';
  }

  onMove(event: PointerEvent): void {
    if (event.pointerId !== this.pointerId) return;
    const dx = event.clientX - this.startX;
    const dy = event.clientY - this.startY;
    if (this.intent === 'pending') {
      this.intent = swipeIntent(dx, dy);
      if (this.intent === 'vertical') {
        this.pointerId = null;
        return;
      }
      if (this.intent === 'horizontal') this.host.nativeElement.style.transition = 'none';
    }
    if (this.intent !== 'horizontal') return;
    this.dx = Math.max(0, dx);
    this.host.nativeElement.style.transform = `translateX(${this.dx}px)`;
  }

  onUp(event: PointerEvent): void {
    if (event.pointerId !== this.pointerId) return;
    const closes = this.intent === 'horizontal' && isCloseSwipe(this.dx, event.clientY - this.startY);
    const el = this.host.nativeElement;
    this.pointerId = null;
    if (closes) {
      // Sortie par la droite, puis le routeur retire le panneau : on garde la position jusque-là.
      el.style.transition = '';
      el.style.transform = 'translateX(100%)';
      this.swipeClose.emit();
      setTimeout(() => this.clearInline(), 400);
    } else {
      this.reset();
    }
  }

  reset(): void {
    this.pointerId = null;
    this.intent = 'pending';
    this.dx = 0;
    this.clearInline();
  }

  private clearInline(): void {
    const el = this.host.nativeElement;
    el.style.transition = '';
    el.style.transform = '';
  }
}
